import { defineStore, acceptHMRUpdate } from 'pinia'
import type { DateTime } from 'luxon'
import { NoteService } from '@/api/client'
import { useConfigStore } from './ConfigStore'

type TDailyNote = {
  day: string
  lastUpdated: string
  note: string
  employeeId: number
  updatedOnDevice?: boolean
}

type TState = {
  daily_notes: TDailyNote[]
}

const initialState: TState = {
  daily_notes: [],
}

const useNoteStore = defineStore('noteStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    getDailyNote: (state) => {
      return (employeeId: number, day: DateTime): TDailyNote | undefined => {
        return state.daily_notes.find(
          (note) =>
            note.day === day.toISODate() && note.employeeId === employeeId,
        )
      }
    },
  },

  actions: {
    saveDailyNote(note: TDailyNote) {
      console.log('saving note', note)
      note.updatedOnDevice = true
      const index = this.daily_notes.findIndex(
        (n) => n.day === note.day && n.employeeId === note.employeeId,
      )
      if (index === -1) {
        this.daily_notes.push(note)
      } else {
        this.daily_notes[index] = note
      }
    },
    async sync() {
      if (!useConfigStore().daily_notes_enabled) return
      await this.push()
      await this.pull()
    },
    async push() {
      console.log('pushing NoteStore')
      const newNotes = this.daily_notes.filter((note) => note.updatedOnDevice)
      for (const note of newNotes) {
        try {
          await NoteService.storeNote({
            day: note.day,
            note: note.note,
            employee_id: note.employeeId,
            last_update: note.lastUpdated,
          })
        } catch (e) {
          console.error(e)
        }
      }
    },
    async pull() {
      console.log('pulling NoteStore')
      try {
        const response = await NoteService.getNotes()
        this.daily_notes =
          response.data?.map((note) => ({
            day: note.day,
            note: note.note,
            employeeId: note.employee_id,
            lastUpdated: note.last_update,
            updatedOnDevice: false,
          })) || []
      } catch (e) {
        console.error(e)
      }
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNoteStore, import.meta.hot))
}

export { useNoteStore }
